import { Component, ViewChild, ElementRef, AfterViewInit, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'tea-shop-fe-hero',
    templateUrl: './hero.component.html',
    styleUrls: ['./hero.component.css']
})
export class HeroComponent implements AfterViewInit, OnInit, OnDestroy{
  private logger = inject(NGXLogger);

  // This is the loop of the steam
  startLoop = 9.6;
  endLoop = 29;

  // This is the reveal for the underlying elements as it clips the vide element!
  revealStart = 6;
  revealEnd = 6 + (29/30); //frame 29, 30 frames/sec = 0.96666666667

  // For animating the clip we have the target clip percentage and the current clip percentage
  clipPercentage = 41.5;
  currentClipPercentage = 0;

  // Use for tracking the actual clip animation
  animationFrameId: number | null = null;
  isRevealing = false;
  lastVideoTime = 0;

  // movieSrc = 'assets/hero.mp4';
  // movieSrc = 'assets/hero2.mp4';
  movieSrc = 'assets/hero3.mp4';

  firstRun = false;

  // We need references to these elements for all the funky animation and video stuff
  @ViewChild('heromov', { static: false })
  videoElement!: ElementRef;
  @ViewChild('videoContainer', { static: false })
  videoContainer!: ElementRef;

  ngOnInit() {
    document.documentElement.style.setProperty('--clip-percentage', `0%`);
  }

  ngAfterViewInit(): void {
    const videoElement: HTMLVideoElement = this.videoElement?.nativeElement;
    if (!videoElement) {
      this.logger.log('Video element not found');
      return;
    }
    this.logger.log('Video element found');

    videoElement.addEventListener('playing', () => {
      this.logger.log('Playing');
      if (!this.animationFrameId) {
        this.animationLoop();
      }
    });
    videoElement.addEventListener('canplay', () => {
      videoElement.muted = true;
      videoElement.play();
      this.logger.log('autoplay set');
    });
    videoElement.addEventListener('timeupdate', () => {
      if (videoElement.currentTime >= this.endLoop) {
        this.logger.log('Looping');
        videoElement.currentTime = this.startLoop;
      }

      // The revealing animation only happens during these times
      this.isRevealing = videoElement.currentTime >= this.revealStart && videoElement.currentTime <= this.revealEnd;
    });
    videoElement.addEventListener('loadedmetadata', () => {
      this.adjustContainerHeight();
    });
  }

  // Linear interpolation function
  lerp(start: number, end: number, t: number): number {
    return start * (1 - t) + end * t;
  }

  // The main reveal animation loop
  animationLoop() {
    if (!this.videoElement?.nativeElement) {
      return;
    }

    const videoElement: HTMLVideoElement = this.videoElement.nativeElement;

    if (this.isRevealing) {
      const t = (videoElement.currentTime - this.revealStart) / (this.revealEnd - this.revealStart);
      this.currentClipPercentage = Math.min(this.lerp(0, this.clipPercentage, t), this.clipPercentage);
      document.documentElement.style.setProperty('--clip-percentage', `${this.currentClipPercentage}%`);
      this.logger.trace(`Clip percentage: ${this.currentClipPercentage}`);
      this.adjustContainerHeight();
    }
    this.animationFrameId = requestAnimationFrame(() => this.animationLoop());
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustContainerHeight();
    this.logger.log('Resized');
  }

  private adjustContainerHeight() {
    const videoElement: HTMLVideoElement = this.videoElement.nativeElement;
    const videoContainer: HTMLElement = this.videoContainer.nativeElement;
    const visiblePercent = (100 - this.currentClipPercentage) / 100;

    const aspectRatio = videoElement.videoWidth / videoElement.videoHeight;
    const currentWidth = videoContainer.offsetWidth;
    const scaledHeight = currentWidth / aspectRatio;

    const newHeight = scaledHeight * visiblePercent;
    videoContainer.style.height = `${newHeight}px`;
  }

  ngOnDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}
